import { deriveFunctionsFromCalibre } from '../../services/firebase';
import type { CustomCalibreEntry } from '../../services/firebase';
import {
  resolveCalibreForRef,
  type ResolvedRefCalibre,
} from '../../services/calibreResolution';
import { isTierStaleOnCalibre } from './aiCalibreApply';
import type { WatchReference } from '../../types';

/**
 * Which legacy fields a calibre write still populates alongside the
 * authoritative `calibreId`.
 *
 * Both come from `destructive` feature flags defaulting to ON, so nothing here
 * changes behaviour until the calibres-as-modules cutover deliberately turns
 * one off. See `calibre.dualWriteLegacyMovement` /
 * `calibre.dualWriteLegacyCalibreString` in services/featureFlagsRegistry.ts.
 */
export interface CalibreDualWriteFlags {
  /** Keep writing `movement`, `movementType` and `functions` off the calibre spec. */
  legacyMovement: boolean;
  /** Keep writing the free-text `calibre` string. */
  legacyCalibreString: boolean;
}

export interface CalibreBulkUpdate {
  /** The Firestore payload, keyed by field path. Empty means "write nothing". */
  updates: Record<string, unknown>;
  /** The resolved calibre, or `null` when the typed string named none. */
  resolved: ResolvedRefCalibre<CustomCalibreEntry> | null;
}

/**
 * Flatten a calibre spec into the legacy `movement` object, dropping every
 * empty value. Firestore rejects `undefined` outright, and writing `''` or `[]`
 * over a field the reference already carries would erase real data rather than
 * enrich it.
 *
 * `movement.type` is the one renamed key — the calibre spec calls it
 * `movementType`. The list is spelled out rather than derived so that adding a
 * field to `CalibreData` is a deliberate decision here, not an automatic one.
 */
function buildLegacyMovement(
  d: CustomCalibreEntry['data'],
): Record<string, unknown> {
  const raw: Record<string, unknown> = {
    baseMovement: d.baseMovement,
    type: d.movementType,
    jewels: d.jewels,
    powerReserveHours: d.powerReserveHours,
    frequencyVph: d.frequencyVph,
    hacking: d.hacking,
    manualWinding: d.manualWinding,
    quickSetDate: d.quickSetDate,
    isChronometer: d.isChronometer,
    isCosc: d.isCosc,
    isGlashuetteRegulation: d.isGlashuetteRegulation,
    isGenevaSeal: d.isGenevaSeal,
    isGrandSeikoStandard: d.isGrandSeikoStandard,
    isMETAS: d.isMETAS,
    isChronograph: d.isChronograph,
    isGMT: d.isGMT,
    isInHouse: d.isInHouse,
    columnWheel: d.columnWheel,
    doubleBarrel: d.doubleBarrel,
    diameterMm: d.diameterMm,
    heightMm: d.heightMm,
    escapement: d.escapement,
    liftAngleDegrees: d.liftAngleDegrees,
    windingDirection: d.windingDirection,
    antiShockSystem: d.antiShockSystem,
    rotorType: d.rotorType,
    hairspring: d.hairspring,
    regulatorSystem: d.regulatorSystem,
    countryOfManufacture: d.countryOfManufacture,
    serviceIntervalYears: d.serviceIntervalYears,
    lignes: d.lignes,
    batteryType: d.batteryType,
    batteryLifeYears: d.batteryLifeYears,
    // Array fields (complications/functions)
    chronograph: d.chronograph,
    date: d.date,
    hands: d.hands,
    additionals: d.additionals,
    astronomical: d.astronomical,
    acoustic: d.acoustic,
    // Durability
    shockResistanceG: d.shockResistanceG,
    antiMagneticAm: d.antiMagneticAm,
    antiMagneticGauss: d.antiMagneticGauss,
    casingDiameterMm: d.casingDiameterMm,
    manualWindingDirection: d.manualWindingDirection,
  };
  return Object.fromEntries(
    Object.entries(raw).filter(([, v]) => {
      if (v === undefined || v === null) return false;
      if (typeof v === 'string' && v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }),
  );
}

/**
 * Build the Firestore payload for a bulk "set calibre" across a selection.
 *
 * One resolver decides everything. The `calibreId` link and any legacy movement
 * specs written beside it both come off the entry
 * {@link resolveCalibreForRef} returned, so a reference can never end up
 * pointing at one calibre while carrying another's specs — the failure mode a
 * second, independent lookup for the spec half would allow.
 *
 * The two flags subtract from the payload; neither adds anything. `calibreId`
 * is written whenever the string resolves, because that link is the migration's
 * destination rather than one of the legacy writes being retired.
 *
 * An EMPTY `updates` is a real, expected outcome: nothing matched and the
 * legacy string is switched off, so there is genuinely nothing left to write.
 * Callers must treat it as an abort rather than issuing a no-op write to every
 * selected reference — see the caller in ReferenceBulkActionBar.
 *
 * The resolver runs with `crossBrandUnscopedAliases`, which is what this bar
 * has always done and what the bulk importer deliberately does not: a universal
 * AKA matches whichever brand the selection belongs to, however the owning
 * calibre is scoped. Typing "Cal. 2824" over Tissot references still reaches
 * the ETA-owned "ETA 2824-2" that carries that unscoped nickname. An alias
 * scoped to a specific brand is unaffected and still refuses every other brand.
 */
export function buildCalibreBulkUpdate(
  entries: readonly CustomCalibreEntry[],
  value: string,
  brand: { id?: string; name?: string },
  flags: CalibreDualWriteFlags,
): CalibreBulkUpdate {
  const typed = value.trim();
  const resolved = typed
    ? resolveCalibreForRef(entries, typed, brand, { crossBrandUnscopedAliases: true })
    : null;
  const updates: Record<string, unknown> = {};

  if (resolved) {
    updates.calibreId = resolved.calibreId;
  }

  if (flags.legacyCalibreString) {
    // On a match the canonical name replaces whatever alias was typed, which is
    // what made the free-text column converge on one spelling per calibre.
    updates.calibre = resolved ? resolved.entry.name : typed;
  }

  if (resolved && flags.legacyMovement) {
    const d = resolved.entry.data;
    const movement = buildLegacyMovement(d);
    const derivedFunctions = deriveFunctionsFromCalibre(d);
    if (d.movementType) updates.movementType = d.movementType;
    if (Object.keys(movement).length > 0) {
      updates.movement = movement as WatchReference['movement'];
    }
    if (derivedFunctions.length > 0) updates.functions = derivedFunctions;
  }

  return { updates, resolved };
}

/**
 * The link half of a calibre write, for a write path that touches ONE
 * reference at a time.
 *
 * {@link buildCalibreBulkUpdate} cannot do this: its payload is applied to a
 * whole selection, so it has no single reference's stored `calibreId` or
 * `calibreTierId` to compare against. Every per-ref path — the import mapper,
 * the calibre enricher's per-ref update, the bulk AI apply — needs the same two
 * decisions, so they live here rather than three times over.
 *
 * `calibreId` is written only when it differs from what the reference already
 * carries; an identical value is a no-op write that would still be counted as a
 * field in the enricher's apply preview.
 *
 * The tier half is the orphan-`calibreTierId` guard from the reference editor's
 * AI apply (ReferenceEditor's final staleness check), applied to the bulk
 * paths: a reference may carry a tier chosen on a PREVIOUS calibre, and linking
 * it to a new one leaves that pointer naming a tier the new calibre does not
 * define. The tiers are read off the entry the resolver returned, so "the
 * calibre defines no tiers" is a known fact here rather than a failed lookup —
 * a pointer on such a calibre is dangling like any other.
 *
 * `clearTier` is the sentinel a clear is expressed with, because that differs
 * by write path: `deleteField()` on the two Firestore update paths, `undefined`
 * on the import mapper, which is building a document rather than patching one.
 *
 * A `null` link returns an EMPTY object — never an unlink. Nothing here severs
 * an existing link because a string failed to resolve.
 *
 * A full {@link ResolvedRefCalibre} satisfies {@link CalibreLink}, so a caller
 * that ran the resolver passes its result straight in. The narrower shape is
 * for the one caller that never runs the resolver: the calibre enricher already
 * matched its groups through its own brand-scoped index and would otherwise
 * have to fabricate a `source` it does not know.
 */
export interface CalibreLink {
  /** `custom_calibres/{id}` — what goes in `WatchReference.calibreId`. */
  calibreId: string;
  /** The calibre being linked to. Only its tier list is read. */
  entry: Pick<CustomCalibreEntry, 'data'>;
}

export function buildCalibreLinkFields(
  link: CalibreLink | null,
  ref: { calibreId?: string; calibreTierId?: string },
  options: { clearTier: unknown },
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  if (!link) return fields;

  if ((ref.calibreId || '').trim() !== link.calibreId) {
    fields.calibreId = link.calibreId;
  }

  const tierId = (ref.calibreTierId || '').trim();
  if (tierId && isTierStaleOnCalibre(tierId, link.entry.data?.tiers)) {
    fields.calibreTierId = options.clearTier;
  }

  return fields;
}

/**
 * Fold the calibre write into a bulk AI enrichment patch for one reference.
 *
 * The bulk AI grid writes whatever cells an admin ticked, so accepting the
 * `calibre` column used to write a bare string: no `calibreId`, and no second
 * look at the tier. Both gaps mattered together. The tier column is validated
 * against the tiers of the calibre the reference is linked to WHEN THE RUN
 * STARTS (BulkAIEnrich resolves them up front to send with the call), so a
 * patch that also changes the calibre could store a tier belonging to the
 * calibre being replaced.
 *
 * Order matters: a tier the AI proposed is dropped first, then whatever tier
 * would survive is checked against the newly linked calibre. The distinction is
 * between a suggestion that never lands (delete the key — there is nothing on
 * the document to correct) and a STORED tier the new link orphans (write the
 * clear sentinel).
 *
 * Unlike the import path, an unresolved name here does NOT keep the string
 * alive with the flag off. This is an update path over references that already
 * exist: dropping the cell leaves the stored calibre exactly as it was, which
 * is what the reference editor's AI apply does with the same flag off. Nothing
 * is lost that the reference did not already hold.
 */
export function buildAiEnrichCalibrePatch(
  patch: Record<string, unknown>,
  ref: { calibreId?: string; calibreTierId?: string },
  entries: readonly CustomCalibreEntry[],
  brand: { id?: string; name?: string },
  flags: CalibreDualWriteFlags,
  options: { clearTier: unknown },
): Record<string, unknown> {
  const accepted = typeof patch.calibre === 'string' ? patch.calibre.trim() : '';
  if (!accepted) return patch;

  const next = { ...patch };
  const resolved = resolveCalibreForRef(entries, accepted, brand, {
    crossBrandUnscopedAliases: true,
  });

  if (!flags.legacyCalibreString) delete next.calibre;
  if (resolved) {
    const link = buildCalibreLinkFields(resolved, ref, options);
    if (link.calibreId !== undefined) next.calibreId = link.calibreId;

    const tiers = resolved.entry.data?.tiers;
    const proposed = typeof next.calibreTierId === 'string' ? next.calibreTierId.trim() : '';
    if (proposed && isTierStaleOnCalibre(proposed, tiers)) delete next.calibreTierId;

    const stored = (ref.calibreTierId || '').trim();
    const surviving =
      typeof next.calibreTierId === 'string' ? next.calibreTierId.trim() : stored;
    if (stored && surviving && isTierStaleOnCalibre(surviving, tiers)) {
      next.calibreTierId = options.clearTier;
    }
  }

  return next;
}
